// İSG Portal — Ziyaretçi Yönetimi (Visits — tenant-scoped) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §11 (Ziyaretçi Yönetimi). Backend kanonu; burada **varsayım
// YOK** — endpoint yolları, istek/yanıt alanları ve kimlik kuralları (Result unwrap
// `value`; Create → `value.uuid` + 201; liste → `value` + `total`/`filteredTotal`)
// doğrudan sözleşmeden türetilmiştir. Serileştirme camelCase'dir (§1).
//
// **Tenant izolasyonu (§4b/§11).** Tüm uçlar çağıranın firmasına (token `CompanyId`)
// kapanır; istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın
// ziyareti/ziyaretçisi hiçbir koşulda görünmez (yok gibi `404 VISIT_NOT_FOUND`).
// Ziyaret silme **soft-delete**'tir. En kritik çıktı "şu an sahada kim var" =
// `GET /visits?status=OnSite` (acil durum/tahliye). Liste sırası backend'de check-in
// **azalan**'dır (istemci sort göndermez). `status`/`entryMethod` PascalCase wire.

import { api } from './client';
import type { Empty } from './envelope';

/** Ziyaret durumu — wire değerleri PascalCase (§11 `VisitDto.status`). */
export type VisitStatus = 'OnSite' | 'CheckedOut';

/** Giriş yöntemi — wire değerleri PascalCase (§11 `VisitDto.entryMethod`). */
export type VisitEntryMethod = 'Manual' | 'Kiosk';

/**
 * Ziyaretçi (kişi — master) — api-contract §11 (`VisitorDto`). Firma içinde telefon,
 * yoksa kimlik no ile tekilleştirilir; açık rıza ilk kez `kvkkConsentAt` damgalanır.
 */
export interface VisitorDto {
  uuid: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  email: string | null;
  idNumber: string | null;
  organization: string | null;
  /** KVKK açık rıza zaman damgası (UTC ISO §4). */
  kvkkConsentAt: string | null;
  createdAt: string | null;
}

/** Ziyaret satırındaki özet ziyaretçi bloğu — api-contract §11 (`VisitDto.visitor`). */
export interface VisitVisitorSummary {
  uuid: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  organization: string | null;
}

/**
 * Ziyaret (giriş olayı) — api-contract §11 (`VisitDto`). Saha/şube/ev-sahibi dış
 * cevapta `uuid` + `ad` ile taşınır (sıralı `Id` sızmaz). Zamanlar UTC ISO (§4);
 * yerelleştirme sunum katmanındadır.
 */
export interface VisitDto {
  uuid: string;
  visitor: VisitVisitorSummary;
  siteUuid: string | null;
  siteName: string | null;
  branchUuid: string | null;
  branchName: string | null;
  hostUserUuid: string | null;
  hostName: string | null;
  purpose: string | null;
  status: VisitStatus;
  entryMethod: VisitEntryMethod;
  /** Giriş zamanı — UTC ISO (§4). Liste sırası bunun azalanıdır. */
  checkInAt: string;
  checkOutAt: string | null;
  safetyBriefingAcceptedAt: string | null;
  /** Check-out için üretilen kod/QR (§11). */
  badgeCode: string | null;
  createdAt: string | null;
}

/** Güvenlik brifing metni (firma tekil) + sürüm — api-contract §11.2 (`SafetyBriefingDto`). */
export interface SafetyBriefingDto {
  contentTr: string;
  contentEn: string | null;
  contentDe: string | null;
  /** Yapılandırılmamışsa `0`; her kayıtta artar (ilk kayıt `1`). */
  version: number;
  updatedAt: string | null;
}

/**
 * Check-in isteği — api-contract §11.1 (`POST /visits/check-in`). Kapılar backend'de
 * tüm yazımlardan ÖNCE: alan doğrulama → **KVKK açık rıza** (`kvkkConsent=false` →
 * `400 KVKK_CONSENT_REQUIRED`; rızasız hiçbir PII yazılmaz) → **güvenlik brifing onayı**
 * (`safetyBriefingAccepted=false` → `400 SAFETY_BRIEFING_REQUIRED`). İstemci de her
 * ikisini önden zorunlu tutar (onaysız gönderim engellenir). `siteUuid`/`branchUuid`
 * aynı firmada olmalı; `hostUserUuid` firmanın aktif kullanıcısı olmalı.
 */
export interface VisitCheckInRequest {
  visitor: {
    firstName: string;
    lastName: string;
    phone?: string | null;
    email?: string | null;
    idNumber?: string | null;
    organization?: string | null;
  };
  siteUuid?: string | null;
  branchUuid?: string | null;
  hostUserUuid?: string | null;
  purpose?: string | null;
  kvkkConsent: boolean;
  safetyBriefingAccepted: boolean;
}

/**
 * Ziyaret liste/filtre parametreleri — api-contract §11.1 (`GET /visits`) + §3 paging.
 * `from`/`to` check-in tarih aralığı (UTC ISO); `search` ziyaretçi ad/soyad/telefon.
 * **Sort gönderilmez** — sıra backend'de check-in azalandır.
 */
export interface VisitListParams {
  status?: VisitStatus;
  siteUuid?: string;
  branchUuid?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Güvenlik brifing güncelleme isteği — api-contract §11.2 (`PUT /visits/safety-briefing`).
 * Upsert; her kayıtta `version` otomatik artar. `contentTr` boş → `400 VALIDATION_FAILED`.
 */
export interface SafetyBriefingUpdateRequest {
  contentTr: string;
  contentEn?: string | null;
  contentDe?: string | null;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * `POST /visits/check-in` (`create`) — manuel ziyaretçi girişi. Başarıda yeni ziyaret
 * `value.uuid` (201). KVKK/brifing kapıları backend'de; istemci de önden zorunlu tutar.
 */
export function checkIn(body: VisitCheckInRequest) {
  return api.post<VisitDto>('visits/check-in', { json: body });
}

/**
 * `POST /visits/{uuid}/check-out` (`update`) — çıkış. `checkOutAt=now`,
 * `status=CheckedOut`. Yok/kapsam dışı → `404 VISIT_NOT_FOUND`; zaten çıkmış →
 * `409 VISIT_ALREADY_CHECKED_OUT`.
 */
export function checkOut(uuid: string) {
  return api.post<VisitDto>(`visits/${uuid}/check-out`);
}

/**
 * `GET /visits` (`list`) — firmanın ziyaretleri (sayfalı + filtreli). "Sahada kim var"
 * = `status: 'OnSite'`. Sıra check-in azalan (istemci sort göndermez).
 */
export function listVisits(params: VisitListParams = {}) {
  return api.list<VisitDto>('visits', {
    searchParams: {
      status: params.status,
      siteUuid: nonEmpty(params.siteUuid),
      branchUuid: nonEmpty(params.branchUuid),
      from: params.from,
      to: params.to,
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/** `GET /visits/{uuid}` (`get`) — tek ziyaret (yok/kapsam dışı → `404 VISIT_NOT_FOUND`). */
export function getVisit(uuid: string) {
  return api.get<VisitDto>(`visits/${uuid}`);
}

/** `DELETE /visits/{uuid}` (`delete`) — **soft-delete** (hatalı kayıt). */
export function deleteVisit(uuid: string) {
  return api.delete<Empty>(`visits/${uuid}`);
}

/**
 * `GET /visits/safety-briefing` (`get`) — firmanın aktif brifing metni + sürümü.
 * Yapılandırılmamışsa boş metin + `version = 0` (envelope 200; 204 yok).
 */
export function getSafetyBriefing() {
  return api.get<SafetyBriefingDto>('visits/safety-briefing');
}

/**
 * `PUT /visits/safety-briefing` (`update`) — brifing metnini upsert eder; `version`
 * otomatik artar. `contentTr` boş → `400 VALIDATION_FAILED`.
 */
export function updateSafetyBriefing(body: SafetyBriefingUpdateRequest) {
  return api.put<SafetyBriefingDto>('visits/safety-briefing', { json: body });
}
